import type { Lang } from "@/lib/villa-content";

type Plural = (n: number) => string;

export type BookT = {
  // header / intro
  backToVilla: string;
  inquiryEyebrow: string;
  pageTitle: string;
  pageIntro: string;

  // status
  successTitle: string;
  reference: string;
  errorTitle: string;
  err: {
    pickDates: string;
    overlap: string;
    invalidForm: string;
    unexpected: string;
  };

  // fieldsets
  fsStay: string;
  fsContact: string;
  fsElse: string;

  // calendar
  cal: {
    pickDates: string;
    explanation: string;
    nights: Plural;
    noDates: string;
    overlapWarning: string;
    clear: string;
    showWeekly: string;
    hideWeekly: string;
    bedroomsLine: string;
  };

  // legend
  leg: {
    available: string;
    reserved: string;
    unavailable: string;
    checkoutMorning: string;
    checkinAfternoon: string;
    selected: string;
    onRequest: string;
    legendAria: string;
  };

  // form
  bedrooms: string;
  adults: string;
  children: string;
  babies: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  messageLabel: string;
  messagePlaceholder: string;
  consent: string;
  sending: string;
  send: string;
  select: string;
  /** Warning shown next to the submit button: booking is only confirmed
   *  once renter completes the guest-info form (sent via email). */
  guestInfoRequired?: string;

  // quote
  q: {
    placeholder: string;
    overlap: string;
    title: string;
    summary: (a: { nights: number; bedrooms: number; adults: number; children: number; babies: number }) => string;
    nights: Plural;
    perWeek: string;
    onRequest: string;
    outsideRateCard: (n: number) => string;
    rentalLabel: string;
    rentalHint: string;
    cleaningLabel: string;
    cleaningHint: string;
    linenLabel: (guests: number, per: number) => string;
    linenHint: string;
    babyPackLabel: string;
    babyPackHint: string;
    estimatedTotal: string;
    depositLabel: string;
    depositHint: string;
    depositFooter: string;
    excludes: string;
  };

  // sticky bar
  sticky: {
    summary: (nights: number, guests: number) => string;
    est: (price: string) => string;
    scrollAria: string;
  };

  // misc
  notFound: string;
  errorSomethingWrong: string;
  errorTryAgain: string;
  moreInfo: (label: string) => string;

  // SEO
  seoTitle: string;
  seoDescription: string;
  seoOgDescription: string;
};

const pl = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const BOOK_T: Record<Lang, BookT> = {
  en: {
    backToVilla: "← Back to villa",
    inquiryEyebrow: "Booking inquiry",
    pageTitle: "Let's plan your stay.",
    pageIntro:
      "Tell us when you'd like to come and who's joining. We reply within 24 hours.",
    successTitle: "Thank you. Your inquiry was sent.",
    reference: "Reference",
    errorTitle: "Could not send your inquiry.",
    err: {
      pickDates: "Please pick check-in and check-out dates on the calendar.",
      overlap:
        "Your selected dates overlap an existing booking. Please pick a different range.",
      invalidForm: "Please check the form, some fields look invalid or incomplete.",
      unexpected: "Unexpected error.",
    },
    fsStay: "Your stay",
    fsContact: "Contact details",
    fsElse: "Anything else?",
    cal: {
      pickDates: "Pick your dates",
      explanation:
        "Red dates are unavailable; yellow dates are reserved (max 36h). Half-shaded days are check-in (afternoon) or check-out (morning). Same-day turnover is allowed.",
      nights: (n) => `${n} ${pl(n, "night", "nights")}`,
      noDates: "No dates selected yet.",
      overlapWarning: "⚠ These dates include nights that are already booked.",
      clear: "Clear dates",
      showWeekly: "Show weekly rates by period",
      hideWeekly: "Hide weekly rates by period",
      bedroomsLine: "4 bedrooms · 3 bedrooms · 2 bedrooms",
    },
    leg: {
      available: "Available",
      reserved: "Reserved (max 36h)",
      unavailable: "Unavailable",
      checkoutMorning: "Check-out morning",
      checkinAfternoon: "Check-in afternoon",
      selected: "Selected",
      onRequest: "On request",
      legendAria: "Calendar legend",
    },
    bedrooms: "Bedrooms",
    adults: "Adults (18+)",
    children: "Children (2–17)",
    babies: "Babies (0–24 months)",
    fullName: "Full name",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone",
    country: "Country",
    messageLabel: "Questions or special requests",
    messagePlaceholder: "Airport transfer, rental car, extra info…",
    consent: "By submitting you agree to be contacted about your inquiry.",
    sending: "Sending…",
    send: "Send inquiry",
    guestInfoRequired:
      "Note: your booking is only fully confirmed once you have filled in the guest information form, which we send you by email after your inquiry.",
    select: "Select…",
    q: {
      placeholder: "Pick check-in and check-out dates above to see a live price quote.",
      overlap:
        "Your selected dates overlap an existing booking. Adjust the range to see a quote.",
      title: "Live price quote",
      summary: ({ nights, bedrooms, adults, children, babies }) => {
        const parts = [
          `${nights} ${pl(nights, "night", "nights")}`,
          `${bedrooms} bedrooms`,
          `${adults} ${pl(adults, "adult", "adults")}`,
        ];
        if (children > 0) parts.push(`${children} ${pl(children, "child", "children")}`);
        if (babies > 0) parts.push(`${babies} ${pl(babies, "baby", "babies")}`);
        return parts.join(" · ");
      },
      nights: (n) => `${n} ${pl(n, "night", "nights")}`,
      perWeek: "/ week",
      onRequest: "On request",
      outsideRateCard: (n) =>
        `${n} ${pl(n, "night", "nights")} fall outside the published rate card. We'll quote on request.`,
      rentalLabel: "Rental",
      rentalHint: "Base weekly rental price for your selected dates and number of bedrooms.",
      cleaningLabel: "Cleaning + linen wash",
      cleaningHint: "One-time end-of-stay cleaning and laundry service.",
      linenLabel: (g, per) =>
        `Bed/bath/kitchen linen (${g} ${pl(g, "guest", "guests")} × €${per})`,
      linenHint: "Fresh linen set per guest (€10 per person).",
      babyPackLabel: "Baby pack (cot + high chair)",
      babyPackHint: "Includes travel cot and high chair.",
      estimatedTotal: "Estimated total",
      depositLabel: "Refundable security deposit",
      depositHint:
        "Added to your final balance payment and returned within 14 days of departure, provided no damage.",
      depositFooter:
        "Collected with the final balance payment, returned within 14 days of departure.",
      excludes:
        "Excludes electricity (€0.40/kWh). Refundable security deposit is included in your final balance payment. Final quote confirmed by the owners within 24h.",
    },
    sticky: {
      summary: (n, g) =>
        `${n} ${pl(n, "night", "nights")} · ${g} ${pl(g, "guest", "guests")}`,
      est: (p) => `${p} est.`,
      scrollAria: "Scroll to send inquiry",
    },
    notFound: "Page not found.",
    errorSomethingWrong: "Something went wrong",
    errorTryAgain: "Try again",
    moreInfo: (l) => `More info: ${l}`,
    seoTitle: "Booking inquiry | Casa Mi Pajobe",
    seoDescription:
      "Send a booking inquiry for Casa Mi Pajobe, a private-pool villa in La Nucia, Costa Blanca. Reply within 24 hours.",
    seoOgDescription:
      "Book your stay and get a quote for our villa on the Costa Blanca.",
  },

  nl: {
    backToVilla: "← Terug naar villa",
    inquiryEyebrow: "Boekingsaanvraag",
    pageTitle: "Plan je verblijf.",
    pageIntro:
      "Vertel ons wanneer je wilt komen en met wie. We antwoorden binnen 24 uur.",
    successTitle: "Bedankt. Je aanvraag is verzonden.",
    reference: "Referentie",
    errorTitle: "Je aanvraag kon niet verzonden worden.",
    err: {
      pickDates: "Kies aankomst- en vertrekdatums in de kalender.",
      overlap:
        "Je geselecteerde dagen overlappen met een bestaande boeking. Kies een ander bereik.",
      invalidForm: "Controleer het formulier, sommige velden lijken ongeldig of onvolledig.",
      unexpected: "Onverwachte fout.",
    },
    fsStay: "Je verblijf",
    fsContact: "Contactgegevens",
    fsElse: "Nog iets?",
    cal: {
      pickDates: "Kies je data",
      explanation:
        "Rode datums zijn niet beschikbaar; gele datums zijn gereserveerd (max 36 u). Halfgekleurde dagen zijn aankomst (namiddag) of vertrek (ochtend). Same-day wisseling is mogelijk.",
      nights: (n) => `${n} ${pl(n, "nacht", "nachten")}`,
      noDates: "Nog geen datums geselecteerd.",
      overlapWarning: "⚠ Deze datums bevatten nachten die al geboekt zijn.",
      clear: "Datums wissen",
      showWeekly: "Weektarieven per periode tonen",
      hideWeekly: "Weektarieven per periode verbergen",
      bedroomsLine: "4 slaapkamers · 3 slaapkamers · 2 slaapkamers",
    },
    leg: {
      available: "Beschikbaar",
      reserved: "Gereserveerd (max 36 u)",
      unavailable: "Niet beschikbaar",
      checkoutMorning: "Vertrek ochtend",
      checkinAfternoon: "Aankomst namiddag",
      selected: "Geselecteerd",
      onRequest: "Op aanvraag",
      legendAria: "Kalenderlegende",
    },
    bedrooms: "Slaapkamers",
    adults: "Volwassenen (18+)",
    children: "Kinderen (2–17)",
    babies: "Baby's (0–24 maanden)",
    fullName: "Volledige naam",
    firstName: "Voornaam",
    lastName: "Achternaam",
    email: "E-mail",
    phone: "Telefoon",
    country: "Land",
    messageLabel: "Vragen of bijzondere wensen",
    messagePlaceholder: "Luchthaventransfer, huurauto, extra info…",
    consent: "Door te verzenden ga je akkoord met contact over je aanvraag.",
    sending: "Verzenden…",
    send: "Aanvraag versturen",
    guestInfoRequired:
      "Let op: je boeking is pas definitief bevestigd zodra je het guest-info formulier hebt ingevuld. We sturen je dit formulier per e-mail na je aanvraag.",
    select: "Kies…",
    q: {
      placeholder: "Kies hierboven aankomst- en vertrekdatums voor een live prijsopgave.",
      overlap:
        "Je geselecteerde dagen overlappen met een bestaande boeking. Pas het bereik aan.",
      title: "Live prijsopgave",
      summary: ({ nights, bedrooms, adults, children, babies }) => {
        const parts = [
          `${nights} ${pl(nights, "nacht", "nachten")}`,
          `${bedrooms} slaapkamers`,
          `${adults} ${pl(adults, "volwassene", "volwassenen")}`,
        ];
        if (children > 0) parts.push(`${children} ${pl(children, "kind", "kinderen")}`);
        if (babies > 0) parts.push(`${babies} ${pl(babies, "baby", "baby's")}`);
        return parts.join(" · ");
      },
      nights: (n) => `${n} ${pl(n, "nacht", "nachten")}`,
      perWeek: "/ week",
      onRequest: "Op aanvraag",
      outsideRateCard: (n) =>
        `${n} ${pl(n, "nacht", "nachten")} valt buiten de tarievenlijst. We maken op aanvraag een offerte.`,
      rentalLabel: "Huur",
      rentalHint: "Basisweekprijs voor de gekozen data en aantal slaapkamers.",
      cleaningLabel: "Schoonmaak + linnenwas",
      cleaningHint: "Eenmalige eindschoonmaak en wasservice.",
      linenLabel: (g, per) =>
        `Bed-/bad-/keukenlinnen (${g} ${pl(g, "gast", "gasten")} × €${per})`,
      linenHint: "Verse linnenset per gast (€10 per persoon).",
      babyPackLabel: "Babypakket (bedje + kinderstoel)",
      babyPackHint: "Inclusief reisbed en kinderstoel.",
      estimatedTotal: "Geschat totaal",
      depositLabel: "Terugbetaalbare waarborg",
      depositHint:
        "Wordt bij de saldobetaling opgenomen en binnen 14 dagen na vertrek terugbetaald, mits geen schade.",
      depositFooter:
        "Inbegrepen in de saldobetaling, binnen 14 dagen na vertrek terugbetaald.",
      excludes:
        "Exclusief elektriciteit (€0,40/kWh). De terugbetaalbare waarborg is opgenomen in de saldobetaling. Definitieve offerte binnen 24 u door de eigenaars bevestigd.",
    },
    sticky: {
      summary: (n, g) =>
        `${n} ${pl(n, "nacht", "nachten")} · ${g} ${pl(g, "gast", "gasten")}`,
      est: (p) => `${p} geschat`,
      scrollAria: "Scroll naar aanvraag versturen",
    },
    notFound: "Pagina niet gevonden.",
    errorSomethingWrong: "Er ging iets mis",
    errorTryAgain: "Opnieuw proberen",
    moreInfo: (l) => `Meer info: ${l}`,
    seoTitle: "Boekingsaanvraag | Casa Mi Pajobe",
    seoDescription:
      "Stuur een boekingsaanvraag voor Casa Mi Pajobe, een villa met privézwembad in La Nucia, Costa Blanca. Antwoord binnen 24 uur.",
    seoOgDescription:
      "Boek uw verblijf en ontvang een offerte voor onze villa aan de Costa Blanca.",
  },

  fr: {
    backToVilla: "← Retour à la villa",
    inquiryEyebrow: "Demande de réservation",
    pageTitle: "Planifions votre séjour.",
    pageIntro:
      "Dites-nous quand vous souhaitez venir et avec qui. Nous répondons sous 24 heures.",
    successTitle: "Merci. Votre demande a été envoyée.",
    reference: "Référence",
    errorTitle: "Votre demande n'a pas pu être envoyée.",
    err: {
      pickDates: "Veuillez choisir les dates d'arrivée et de départ dans le calendrier.",
      overlap:
        "Les dates sélectionnées chevauchent une réservation existante. Veuillez choisir une autre période.",
      invalidForm:
        "Veuillez vérifier le formulaire, certains champs semblent invalides ou incomplets.",
      unexpected: "Erreur inattendue.",
    },
    fsStay: "Votre séjour",
    fsContact: "Coordonnées",
    fsElse: "Autre chose ?",
    cal: {
      pickDates: "Choisissez vos dates",
      explanation:
        "Les dates en rouge sont indisponibles ; en jaune, réservées (max 36 h). Les jours à demi-teinte sont l'arrivée (après-midi) ou le départ (matin). La rotation le même jour est possible.",
      nights: (n) => `${n} ${pl(n, "nuit", "nuits")}`,
      noDates: "Aucune date sélectionnée.",
      overlapWarning: "⚠ Ces dates incluent des nuits déjà réservées.",
      clear: "Effacer les dates",
      showWeekly: "Afficher les tarifs hebdomadaires par période",
      hideWeekly: "Masquer les tarifs hebdomadaires par période",
      bedroomsLine: "4 chambres · 3 chambres · 2 chambres",
    },
    leg: {
      available: "Disponible",
      reserved: "Réservé (max 36 h)",
      unavailable: "Indisponible",
      checkoutMorning: "Départ le matin",
      checkinAfternoon: "Arrivée l'après-midi",
      selected: "Sélectionné",
      onRequest: "Sur demande",
      legendAria: "Légende du calendrier",
    },
    bedrooms: "Chambres",
    adults: "Adultes (18+)",
    children: "Enfants (2–17)",
    babies: "Bébés (0–24 mois)",
    fullName: "Nom complet",
    firstName: "Prénom",
    lastName: "Nom",
    email: "E-mail",
    phone: "Téléphone",
    country: "Pays",
    messageLabel: "Questions ou demandes particulières",
    messagePlaceholder: "Transfert aéroport, location de voiture, infos…",
    consent: "En envoyant, vous acceptez d'être contacté à propos de votre demande.",
    sending: "Envoi…",
    send: "Envoyer la demande",
    guestInfoRequired:
      "Attention : votre réservation n'est définitivement confirmée qu'après avoir rempli le formulaire d'informations sur les voyageurs, que nous vous envoyons par e-mail après votre demande.",
    select: "Choisir…",
    q: {
      placeholder: "Choisissez vos dates ci-dessus pour voir un devis en direct.",
      overlap:
        "Vos dates sélectionnées chevauchent une réservation existante. Ajustez la période pour voir un devis.",
      title: "Devis en direct",
      summary: ({ nights, bedrooms, adults, children, babies }) => {
        const parts = [
          `${nights} ${pl(nights, "nuit", "nuits")}`,
          `${bedrooms} chambres`,
          `${adults} ${pl(adults, "adulte", "adultes")}`,
        ];
        if (children > 0) parts.push(`${children} ${pl(children, "enfant", "enfants")}`);
        if (babies > 0) parts.push(`${babies} ${pl(babies, "bébé", "bébés")}`);
        return parts.join(" · ");
      },
      nights: (n) => `${n} ${pl(n, "nuit", "nuits")}`,
      perWeek: "/ semaine",
      onRequest: "Sur demande",
      outsideRateCard: (n) =>
        `${n} ${pl(n, "nuit", "nuits")} hors de la grille tarifaire publiée. Devis sur demande.`,
      rentalLabel: "Location",
      rentalHint: "Prix de location hebdomadaire de base pour vos dates et le nombre de chambres.",
      cleaningLabel: "Ménage + linge",
      cleaningHint: "Ménage de fin de séjour et service de blanchisserie inclus une fois.",
      linenLabel: (g, per) =>
        `Linge lit/bain/cuisine (${g} ${pl(g, "personne", "personnes")} × ${per} €)`,
      linenHint: "Jeu de linge propre par personne (10 € par personne).",
      babyPackLabel: "Pack bébé (lit + chaise haute)",
      babyPackHint: "Comprend un lit parapluie et une chaise haute.",
      estimatedTotal: "Total estimé",
      depositLabel: "Caution remboursable",
      depositHint:
        "Ajoutée au paiement du solde et restituée sous 14 jours après votre départ, en l'absence de dégâts.",
      depositFooter:
        "Encaissée avec le solde, restituée sous 14 jours après votre départ.",
      excludes:
        "Hors électricité (0,40 €/kWh). La caution remboursable est incluse dans le paiement du solde. Devis final confirmé par les propriétaires sous 24 h.",
    },
    sticky: {
      summary: (n, g) =>
        `${n} ${pl(n, "nuit", "nuits")} · ${g} ${pl(g, "personne", "personnes")}`,
      est: (p) => `${p} est.`,
      scrollAria: "Faire défiler jusqu'à envoyer la demande",
    },
    notFound: "Page introuvable.",
    errorSomethingWrong: "Une erreur est survenue",
    errorTryAgain: "Réessayer",
    moreInfo: (l) => `Plus d'infos : ${l}`,
    seoTitle: "Demande de réservation | Casa Mi Pajobe",
    seoDescription:
      "Envoyez une demande de réservation pour Casa Mi Pajobe, villa avec piscine privée à La Nucia, Costa Blanca. Réponse sous 24 h.",
    seoOgDescription:
      "Réservez votre séjour et demandez un devis pour notre villa sur la Costa Blanca.",
  },

  de: {
    backToVilla: "← Zurück zur Villa",
    inquiryEyebrow: "Buchungsanfrage",
    pageTitle: "Planen wir Ihren Aufenthalt.",
    pageIntro:
      "Sagen Sie uns, wann Sie kommen möchten und wer mitkommt. Wir antworten innerhalb von 24 Stunden.",
    successTitle: "Vielen Dank. Ihre Anfrage wurde gesendet.",
    reference: "Referenz",
    errorTitle: "Ihre Anfrage konnte nicht gesendet werden.",
    err: {
      pickDates: "Bitte wählen Sie An- und Abreisedatum im Kalender.",
      overlap:
        "Die gewählten Daten überschneiden sich mit einer bestehenden Buchung. Bitte wählen Sie einen anderen Zeitraum.",
      invalidForm:
        "Bitte prüfen Sie das Formular, einige Felder scheinen ungültig oder unvollständig.",
      unexpected: "Unerwarteter Fehler.",
    },
    fsStay: "Ihr Aufenthalt",
    fsContact: "Kontaktdaten",
    fsElse: "Sonst noch etwas?",
    cal: {
      pickDates: "Daten auswählen",
      explanation:
        "Rote Daten sind nicht verfügbar; gelbe Daten sind reserviert (max. 36 h). Halb schraffierte Tage sind Anreise (nachmittags) oder Abreise (vormittags). Wechsel am selben Tag ist möglich.",
      nights: (n) => `${n} ${pl(n, "Nacht", "Nächte")}`,
      noDates: "Noch keine Daten ausgewählt.",
      overlapWarning: "⚠ Diese Auswahl enthält bereits gebuchte Nächte.",
      clear: "Daten löschen",
      showWeekly: "Wochenpreise nach Zeitraum anzeigen",
      hideWeekly: "Wochenpreise nach Zeitraum ausblenden",
      bedroomsLine: "4 Schlafzimmer · 3 Schlafzimmer · 2 Schlafzimmer",
    },
    leg: {
      available: "Verfügbar",
      reserved: "Reserviert (max. 36 h)",
      unavailable: "Nicht verfügbar",
      checkoutMorning: "Abreise vormittags",
      checkinAfternoon: "Anreise nachmittags",
      selected: "Ausgewählt",
      onRequest: "Auf Anfrage",
      legendAria: "Kalenderlegende",
    },
    bedrooms: "Schlafzimmer",
    adults: "Erwachsene (18+)",
    children: "Kinder (2–17)",
    babies: "Babys (0–24 Monate)",
    fullName: "Vollständiger Name",
    firstName: "Vorname",
    lastName: "Nachname",
    email: "E-Mail",
    phone: "Telefon",
    country: "Land",
    messageLabel: "Fragen oder besondere Wünsche",
    messagePlaceholder: "Flughafentransfer, Mietwagen, weitere Infos…",
    consent: "Mit dem Absenden stimmen Sie zu, zu Ihrer Anfrage kontaktiert zu werden.",
    sending: "Wird gesendet…",
    send: "Anfrage senden",
    guestInfoRequired:
      "Hinweis: Ihre Buchung ist erst dann endgültig bestätigt, wenn Sie das Gäste-Informationsformular ausgefüllt haben. Wir senden es Ihnen nach Ihrer Anfrage per E-Mail.",
    select: "Auswählen…",
    q: {
      placeholder: "Wählen Sie oben An- und Abreisedatum für ein Live-Preisangebot.",
      overlap:
        "Die gewählten Daten überschneiden sich mit einer Buchung. Bitte Zeitraum anpassen.",
      title: "Live-Preisangebot",
      summary: ({ nights, bedrooms, adults, children, babies }) => {
        const parts = [
          `${nights} ${pl(nights, "Nacht", "Nächte")}`,
          `${bedrooms} Schlafzimmer`,
          `${adults} ${pl(adults, "Erwachsener", "Erwachsene")}`,
        ];
        if (children > 0) parts.push(`${children} ${pl(children, "Kind", "Kinder")}`);
        if (babies > 0) parts.push(`${babies} ${pl(babies, "Baby", "Babys")}`);
        return parts.join(" · ");
      },
      nights: (n) => `${n} ${pl(n, "Nacht", "Nächte")}`,
      perWeek: "/ Woche",
      onRequest: "Auf Anfrage",
      outsideRateCard: (n) =>
        `${n} ${pl(n, "Nacht", "Nächte")} außerhalb der veröffentlichten Preisliste. Angebot auf Anfrage.`,
      rentalLabel: "Miete",
      rentalHint: "Basis-Wochenmiete für die gewählten Daten und Anzahl Schlafzimmer.",
      cleaningLabel: "Endreinigung + Wäsche",
      cleaningHint: "Einmalige Endreinigung und Wäscheservice.",
      linenLabel: (g, per) =>
        `Bett-/Bad-/Küchenwäsche (${g} ${pl(g, "Gast", "Gäste")} × ${per} €)`,
      linenHint: "Frisches Wäscheset pro Gast (10 € pro Person).",
      babyPackLabel: "Baby-Paket (Reisebett + Hochstuhl)",
      babyPackHint: "Beinhaltet Reisebett und Hochstuhl.",
      estimatedTotal: "Geschätzter Gesamtpreis",
      depositLabel: "Erstattbare Kaution",
      depositHint:
        "Wird zur Restzahlung hinzugefügt und innerhalb von 14 Tagen nach Abreise zurückerstattet, sofern keine Schäden.",
      depositFooter:
        "Mit der Restzahlung eingezogen, innerhalb von 14 Tagen nach Abreise zurückerstattet.",
      excludes:
        "Ohne Strom (0,40 €/kWh). Die erstattbare Kaution ist in der Restzahlung enthalten. Endgültiges Angebot von den Eigentümern innerhalb von 24 h bestätigt.",
    },
    sticky: {
      summary: (n, g) =>
        `${n} ${pl(n, "Nacht", "Nächte")} · ${g} ${pl(g, "Gast", "Gäste")}`,
      est: (p) => `ca. ${p}`,
      scrollAria: "Zum Anfrage senden scrollen",
    },
    notFound: "Seite nicht gefunden.",
    errorSomethingWrong: "Etwas ist schiefgelaufen",
    errorTryAgain: "Erneut versuchen",
    moreInfo: (l) => `Mehr Infos: ${l}`,
    seoTitle: "Buchungsanfrage | Casa Mi Pajobe",
    seoDescription:
      "Senden Sie eine Buchungsanfrage für Casa Mi Pajobe, eine Villa mit Privatpool in La Nucia, Costa Blanca. Antwort innerhalb von 24 Stunden.",
    seoOgDescription:
      "Buchen Sie Ihren Aufenthalt und erhalten Sie ein Angebot für unsere Villa an der Costa Blanca.",
  },
};

export function bookHead(lang: Lang) {
  const t = BOOK_T[lang];
  const path = lang === "en" ? "/book" : `/book/${lang}`;
  const url = `https://www.casamipajobe.com${path}`;
  return {
    meta: [
      { title: t.seoTitle },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      { name: "description", content: t.seoDescription },
      { property: "og:title", content: t.seoTitle },
      { property: "og:description", content: t.seoOgDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: lang },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: t.seoTitle,
          url,
          description: t.seoDescription,
          inLanguage: lang,
          isPartOf: { "@id": "https://www.casamipajobe.com/#website" },
          about: { "@id": "https://www.casamipajobe.com/#lodging" },
          potentialAction: {
            "@type": "ReserveAction",
            target: url,
            result: {
              "@type": "LodgingReservation",
              name: "Villa booking inquiry",
            },
          },
        }),
      },
    ],
  };
}